import { HideContainer } from '@components/hide-container';
import { SelectOption } from '@components/select';
import { InfoButton, Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { FieldMappingEditorValue } from '@components/text-editors/addons/field-mapping';
import { useScopedTranslate } from '@locale/translate';

import { useJourneyAction, useUpdateJourneyAction } from 'api/journey-actions/hooks';
import { TimeSavedData } from 'api/journeys/requests';
import { JourneyActionDetailed, JourneyActionValuesTypes } from 'api/types';
import { ACTION_TYPES } from 'ui/actions/action-config';
import { TimeSavedContainer } from 'ui/helpers';
import { ExecutionMethod } from 'ui/journey-builder/execution-method';
import {
  ActionFooter,
  ActionHeader,
  ActionSidebarLoading,
  DescriptionFormField,
  SidebarProps,
  createActionForm,
  useCreateActionFormStore,
  useVariableGroups,
} from 'ui/journey-builder/node-types/common';

import { FormWithUnsavedChangesPrompt } from '../../journey-builder/unsaved-changes-prompt';
import { TriggerMethod } from '../common';
import { getFieldMappingId } from '../helpers';

type AnonymizeTextData = {
  id: string;
  description: string;
  isAutomatic: boolean;
  assignees: SelectOption[];
  executionMethod: TriggerMethod;
  input: FieldMappingEditorValue[];
  marker: string;
} & TimeSavedData;

const ACTION_TYPE = ACTION_TYPES.anonymize_text;
const Form = createActionForm<AnonymizeTextData>();

export const SidebarContent = (props: SidebarProps) => {
  const { journeyActionId } = props;
  const { data, isLoading } = useJourneyAction(journeyActionId);

  if (isLoading || !data) {
    return <ActionSidebarLoading actionType={ACTION_TYPE} {...props} />;
  }

  return (
    <Sidebar>
      <ActionHeader actionType={ACTION_TYPE} {...props}>
        <InfoButton>
          <Info />
        </InfoButton>
      </ActionHeader>
      <Sidebar.Content>
        <AnonymizeText journeyActionId={journeyActionId} action={data} />
      </Sidebar.Content>
    </Sidebar>
  );
};

const Info = () => {
  const { t } = useScopedTranslate(sidebarLocale);
  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutAnonymize')} />
      <Text text={t('info')} />
      <Text text={t('info2')} />
      <Text text={t('info3')} />
    </Stack>
  );
};

const AnonymizeText = ({
  journeyActionId,
  action,
}: {
  journeyActionId: string;
  action: JourneyActionDetailed;
}) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const formStore = useCreateActionFormStore();
  const isRequired = formStore.useFieldValue('executionMethod') === 'automatic';
  const values = action.values as JourneyActionValuesTypes['anonymize_text'];
  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyAction(journeyActionId);
  const variableGroups = useVariableGroups({
    actionVariables: action.actionVariables,
    journeyId: action.journey,
    types: ['text', 'paragraph'],
  });
  const fieldMappingId = getFieldMappingId(action.journey);

  const handleSave = (data: AnonymizeTextData) =>
    editMutation.mutateAsync({
      description: data.description,
      isAutomatic: data.executionMethod === 'automatic',
      action: ACTION_TYPE,
      assignees: data.executionMethod === 'manual' ? data.assignees.map((user) => user.value) : [],
      values: {
        input: data.input,
        marker: data.marker || '',
      },
      timeSaved: {
        days: data.days,
        hours: data.hours,
        minutes: data.minutes,
      },
    });

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={handleSave}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <DescriptionFormField defaultValue={action.description} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('triggerMethod')}>
          <ExecutionMethod
            assignees={action.assignees}
            isAutomatic={action.isAutomatic}
            formStore={formStore}
            hideLabel
          />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('text')}>
          <Stack space={4}>
            <Form.FieldMappingField
              id="input"
              label={t('text')}
              defaultValue={values?.input}
              singleLine
              plaintext
              variableGroups={variableGroups}
              fieldMappingId={fieldMappingId}
              validators={isRequired ? ['required'] : []}
            />
            <Form.TextField
              id="marker"
              defaultValue={values?.marker ?? '[REDACTED]'}
              label={t('marker')}
            />
          </Stack>
        </HideContainer>
        <Sidebar.Separator />
        <TimeSavedContainer {...action.timeSaved} />
      </Stack>
      <ActionFooter action={action} isDisabled={!isDirty || editMutation.isLoading} form={Form} />
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    aboutAnonymize: 'About anonymizing text',
    info: 'Anonymize text by removing personally identifiable information (PII) such as names, addresses, phone numbers, and email addresses. Simply choose your input text and receive a redacted version with sensitive information removed. You can also specify a custom redaction marker (default [REDACTED]).',
    info2:
      'Common use cases include preparing documents for sharing, compliance with privacy regulations, or protecting confidential information before publication.',
    info3:
      'Please note: This is an automated process that uses computer algorithms to identify and redact PII. While designed to be thorough, it may not catch all instances of sensitive information or might occasionally redact non-sensitive content. Review is recommended for highly sensitive materials.',
    actionDetails: 'Action details',
    triggerMethod: 'Trigger method',
    text: 'Input text',
    marker: 'Redaction marker',
  },
};
